/**
 * Refusal copy (deliverable 3): the server's refusals in fixed words. By `details.issue.code`
 * first, then by the error's code, else the generic line. No value is ever echoed.
 */
import { ApiError, NetworkError } from '@codex/contracts';

export const GENERIC_REFUSAL = "This change couldn't be saved.";
export const NETWORK_REFUSAL = "The server couldn't be reached. Try again.";

const BY_ISSUE: Readonly<Record<string, string>> = {
  blank: 'A name, icon or color cannot be blank.',
  builtin_type: 'A built-in type cannot be archived.',
  archived_type: 'This type is archived. Restore it first.',
  reference_target: 'A page cannot become a reference type.',
  session_target: 'A page cannot become a session this way.',
  bad_link_type: 'Choose a link type for this reference.',
  temporal_field: 'Dated values are not available for this field yet.',
  kind_mismatch: "These settings don't suit this kind of field.",
  bad_rename: 'An option rename names an option that does not exist.',
  option_in_use: 'Pages still use an option you removed.',
  link_type_fixed: 'A reference keeps the link type and direction it was created with.',
  bad_template: 'The default template must belong to this type.',
  bad_position: 'That position is not available.',
  pair_refused: "This field can't change to that kind.",
  archived_field: 'Restore this field before changing it.',
  wrong_type: "That value doesn't suit this field.",
  too_long: 'That is too long.',
  line_break: "Line breaks aren't allowed here.",
  not_finite: 'Enter a number.',
  not_an_option: 'Choose one of the listed options.',
  duplicate_option: 'Each option must be different.',
  too_many: 'There are too many options.',
  bad_url: 'Enter a web address.',
  bad_date: 'Enter a date.',
  bad_dice: 'Enter dice such as 2d6+1.',
  bad_character: "That contains a character that isn't allowed.",
  bad_option: "An option can't be blank or contain commas or line breaks.",
  unknown_key: "This field's settings include something unknown.",
  bad_direction: 'Choose which way the links point.',
  unknown_field: 'This field no longer exists.',
  not_applicable: "This field doesn't apply to this page.",
  not_a_prop: 'This field is edited through links.',
  unknown_asset: 'That image was not found.',
  unknown_type: 'This type no longer exists.',
};

const BY_CODE: Readonly<Record<string, string>> = {
  field_key_conflict: 'Another field of this type already uses that key.',
  capability_locked:
    "The session type's capability can't be changed, and session and card can't be added.",
  reference_read_only: 'Reference types are read-only.',
  retype_blocked: "This page can't change type while it has a session in progress.",
  version_conflict: 'This page changed. Reload it and try again.',
  document_trashed: 'This page is in the trash.',
};

/** The `details.issue.code` of a refusal, when the server named one. */
export function issueCode(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const issue = (error.body.details as { issue?: { code?: unknown } } | undefined)?.issue;
  return typeof issue?.code === 'string' ? issue.code : null;
}

export function describeRefusal(error: unknown): string {
  if (error instanceof NetworkError) return NETWORK_REFUSAL;
  const issue = issueCode(error);
  if (issue !== null && BY_ISSUE[issue] !== undefined) return BY_ISSUE[issue];
  if (error instanceof ApiError && BY_CODE[error.code] !== undefined)
    return BY_CODE[error.code] ?? GENERIC_REFUSAL;
  return GENERIC_REFUSAL;
}
